// src/components/maps/POILegend.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { POI_TYPE_CONFIG, getPOIIcon, getPOIColor, getPOILabel } from './POIMarker';

/**
 * Legend component showing POI types displayed on the map
 * @param {Object} props
 * @param {Array<string>} props.visibleTypes - Array of POI type codes currently visible on the map
 * @param {boolean} props.compact - Whether to use compact styling (for mobile)
 */
const POILegend = ({ visibleTypes = [], compact = false }) => {
  const [expanded, setExpanded] = useState(!compact);

  // If no visible types provided, show all types
  const typesToShow = visibleTypes.length > 0
    ? visibleTypes
    : Object.keys(POI_TYPE_CONFIG);

  // Filter to only show types that exist in config
  const validTypes = typesToShow.filter(type => POI_TYPE_CONFIG[type]);

  if (validTypes.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: compact ? 8 : 16,
        right: compact ? 8 : 16,
        zIndex: 1000,
        backgroundColor: 'rgba(255,255,255,0.95)',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: compact ? 180 : 220,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          backgroundColor: 'grey.100',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
          Legend
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Legend Items */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1 }}>
          {validTypes.map(type => {
            const IconComponent = getPOIIcon(type);
            const color = getPOIColor(type);
            const label = getPOILabel(type);

            return (
              <Box
                key={type}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: compact ? 20 : 24,
                    height: compact ? 20 : 24,
                    backgroundColor: color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: compact ? 12 : 14,
                      color: 'white',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: compact ? '0.65rem' : '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default POILegend;
